import { getAuth } from 'firebase/auth';
import { DATA_API_BASE_URL } from '../config/api';

const BASE_URL = DATA_API_BASE_URL;

export interface ToggleRealtimeRequest {
  map_id: string;
  enable: boolean;
}

export interface ToggleRealtimeResponse {
  msg: string;
  map_id: string;
  is_monitored: boolean;
  bq_updated_rows: number;
}

export interface MonitoredMapsResponse {
  count: number;
  monitored_map_ids: string[];
}

// Granularity the backend chose for a realtime response. It aggregates
// automatically when the requested range would otherwise blow past the row
// cap (which used to truncate wide ranges to just the newest rows).
export type RealtimeBucket = 'raw' | 'hour' | 'day';

export interface RealtimeRow {
  timestamp: string; // bucketed: the bucket's start (hour/midnight aligned)
  target_id: string;
  target_name: string;
  result?: string; // raw responses only — dropped once aggregated
  subscriber_count: number; // bucketed: the MEAN over the bucket
  subscriber_count_min?: number;
  subscriber_count_max?: number; // use for "peak" — the mean smooths spikes away
  sample_count?: number; // raw samples behind the bucket
}

export interface RealtimeCountryRow {
  timestamp: string;
  target_id: string;
  target_name: string;
  subscriber_count: number;
  country: string;
  subscriber_count_min?: number;
  subscriber_count_max?: number;
  sample_count?: number;
}

export interface RealtimeDataResult {
  rows: RealtimeRow[];
  bucket: RealtimeBucket;
}

export interface RealtimeCountryResult {
  rows: RealtimeCountryRow[];
  bucket: RealtimeBucket;
}

export interface RealtimeBaseline {
  target_id: string;
  target_name: string;
  night_floor: number | null; // null when fewer than the reliability floor's worth of quiet samples
  quiet_samples: number;
  nights_used: number;
  available_nights: string[]; // ascending "YYYY-MM-DD", area-local
  range_from: string | null;
  range_to: string | null;
}

const getAuthHeaders = async (): Promise<HeadersInit> => {
  const auth = getAuth();
  const user = auth.currentUser;

  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (user) {
    try {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    } catch (error) {
      console.error('Failed to get Firebase ID token:', error);
      throw new Error('Authentication required - please log in');
    }
  } else {
    throw new Error('User not authenticated - please log in');
  }

  return headers;
};

export const realtimeApi = {
  async toggleMonitoring(request: ToggleRealtimeRequest): Promise<ToggleRealtimeResponse> {
    console.log('🔔 Toggling realtime monitoring:', request);

    const url = `${BASE_URL}/realtime`;
    console.log('🌐 Request URL:', url);

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Realtime monitoring toggled successfully:', result);
      return result;

    } catch (error) {
      console.error('Failed to toggle realtime monitoring:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  },

  async getMonitoredMaps(): Promise<MonitoredMapsResponse> {
    console.log('📍 Loading monitored maps');

    const url = `${BASE_URL}/maps/monitored`;
    console.log('🌐 Request URL:', url);

    try {
      const headers = await getAuthHeaders();

      const response = await fetch(url, {
        method: 'GET',
        headers
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Monitored maps loaded successfully:', result);
      return result;

    } catch (error) {
      console.error('Failed to load monitored maps:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Network error - check CORS and API availability: ${error.message}`);
      }
      throw error;
    }
  },

  /**
   * Fetch rows from the user's personal realtime view. The maps-api returns them
   * newest-first and supports server-side filters: `targetId` (single target) and
   * a `datetimeFrom`/`datetimeTo` range (ISO 8601). `datetimeTo` defaults to now
   * on the backend, so omit it for "up to now". The dataset is auto-resolved from
   * the user's machine account, so no dataset params are passed.
   */
  async getRealtimeData(
    opts: { limit?: number; targetId?: string; datetimeFrom?: string; datetimeTo?: string; bucket?: RealtimeBucket } = {}
  ): Promise<RealtimeDataResult> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated - please log in');

    const params = new URLSearchParams({ limit: String(opts.limit ?? 10000) });
    if (opts.targetId) params.set('target_id', opts.targetId);
    if (opts.datetimeFrom) params.set('datetime_from', opts.datetimeFrom);
    if (opts.datetimeTo) params.set('datetime_to', opts.datetimeTo);
    // Optional override of the server's automatic granularity (reports want hourly).
    if (opts.bucket) params.set('bucket', opts.bucket);
    const url = `${BASE_URL}/realtime/data?${params.toString()}`;
    console.log('🌐 Realtime data request URL:', url);

    const headers = await getAuthHeaders();
    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    console.log('📡 Realtime data response status:', response.status, response.statusText);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error ${response.status}: ${errorText || response.statusText}`);
    }

    const { rows, bucket } = await response.json();
    return {
      rows: (rows || []).map((row: any) => ({
        ...row,
        timestamp: row.timestamp?.value ?? row.timestamp,
      })),
      // Backends predating the bucketing change omit the field — that response
      // is raw by definition.
      bucket: (bucket as RealtimeBucket) ?? 'raw',
    };
  },

  /**
   * Realtime "subscribers by country" breakdown for the user's monitored areas
   * (GET /realtime/data/country). The backend gates this on the
   * `realtime_data_country` view being enabled — a 403 (surfaced via err.status)
   * means the feature isn't turned on for the account. Rows are per
   * (timestamp, target, country); callers filter/aggregate client-side.
   */
  async getRealtimeDataCountry(
    limit = 10000,
    targetId?: string,
    from?: string,
    to?: string,
    bucketOverride?: RealtimeBucket
  ): Promise<RealtimeCountryResult> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated - please log in');

    const params = new URLSearchParams({ limit: String(limit) });
    // Server-side single-target filter — the backend returns just this target's
    // rows (newest-first), so we don't rely on catching it in an unordered slice.
    if (targetId) params.set('target_id', targetId);
    // Optional date window — the project "by country" view aggregates over the
    // selected range (datetime_to defaults to now server-side).
    if (from) params.set('datetime_from', from);
    if (to) params.set('datetime_to', to);
    if (bucketOverride) params.set('bucket', bucketOverride);
    const url = `${BASE_URL}/realtime/data/country?${params.toString()}`;
    console.log('🌐 Realtime country request URL:', url);

    const headers = await getAuthHeaders();
    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    console.log('📡 Realtime country response status:', response.status, response.statusText);
    if (!response.ok) {
      const errorText = await response.text();
      const err = new Error(
        `API Error ${response.status}: ${errorText || response.statusText}`
      ) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    const { rows, bucket } = await response.json();
    return {
      rows: (rows || []).map((row: any) => ({
        ...row,
        timestamp: row.timestamp?.value ?? row.timestamp,
      })),
      bucket: (bucket as RealtimeBucket) ?? 'raw',
    };
  },

  /**
   * Server-computed night-floor baseline (GET /realtime/baseline). Quiet-hour
   * bucketing and the median are computed backend-side in area-local time —
   * the frontend only does `raw - night_floor`. Omitting targetId returns one
   * entry per target with data (used by the project dashboard). `from`/`to`
   * (YYYY-MM-DD night keys) narrow which nights the floor is measured from;
   * `available_nights` is always the full history regardless of that range.
   */
  async getRealtimeBaseline(
    opts: { targetId?: string; from?: string; to?: string } = {}
  ): Promise<RealtimeBaseline[]> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated - please log in');

    const params = new URLSearchParams();
    if (opts.targetId) params.set('target_id', opts.targetId);
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    const url = `${BASE_URL}/realtime/baseline?${params.toString()}`;
    console.log('🌐 Realtime baseline request URL:', url);

    const headers = await getAuthHeaders();
    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    console.log('📡 Realtime baseline response status:', response.status, response.statusText);
    if (!response.ok) {
      const errorText = await response.text();
      // Surface the status: a 404 means this deployment has no baseline
      // endpoint at all (prod's maps-api doesn't), which callers should treat
      // as "feature absent" rather than "this area has no night-time data".
      const err = new Error(
        `API Error ${response.status}: ${errorText || response.statusText}`
      ) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    const { baselines } = await response.json();
    return baselines || [];
  }
};
